/**
 * screenFeelScope — whose screen a screen-wide feel belongs to (lane AP,
 * 2026-10-09).
 *
 * Screen shake, screen flash, screen tint, hit-stop and slow-mo act on the
 * whole viewport, so they are only honest for something the local player did
 * or had done to them. Before this, a bot Paladin's consecration across the
 * arena flashed every screen in the room: remote casts replay the caster's
 * VFX defs through the same player, and the def-level screen block ran for
 * whoever watched. Rules:
 *
 *  - VFX defs: a play marked `origin: 'remote'` (every play the remote-cast
 *    handler makes) draws its world shapes but skips the def's screen block.
 *    Unmarked plays keep it — local casts, QA playback, world beats.
 *  - Hit impacts: tier shake, hit-stop and the epic flash play when the local
 *    player struck or was struck. An unattributed hit (no attacker on the
 *    wire) keeps them; a hit between two other actors does not.
 */
import { ServiceRegistry } from '../core/ServiceRegistry';
import type { VFXEffectConfig } from './VFXEffectRegistry';

/** Override marker for a VFX play replaying ANOTHER player's cast. */
export const REMOTE_VFX_ORIGIN = 'remote' as const;

interface ScreenEffectSink {
  screenFlash(color: number, duration: number): void;
  screenTint(color: number, duration: number): void;
}

/**
 * Apply a def's screen-wide block unless the play is remote.
 *
 * Plan 29 F8 (lane BS, 2026-10-11): only the TINT survives here. A def's
 * `screenShake` and `screenFlash` no longer reach the screen: they fired on
 * every play of the def — the mage auto flashed the screen purple on every
 * hit, four heals flashed it gold, Whirlwind/Hammer of Wrath/Howl shook it at
 * cast time — whatever the hit weighed. Screen shake and flash now come from
 * one place, the confirmed hit's tier (hitWeight → hitPresentation →
 * `CombatFeedbackManager.triggerImpact`: no shake below medium, the white
 * flash only at epic). The fields stay on the defs as authoring intent.
 */
export function applyDefScreenFeel(
  config: Pick<VFXEffectConfig, 'screenShake' | 'screenFlash' | 'screenTint'>,
  overrides: { readonly [key: string]: unknown } | undefined,
  screenEffects: ScreenEffectSink,
): void {
  if (overrides?.origin === REMOTE_VFX_ORIGIN) return;
  if (config.screenTint) screenEffects.screenTint(config.screenTint.color, config.screenTint.duration);
}

/** True when `id` names the local player (Colyseus session id or engine player id). */
export function isLocalActor(id: string | null | undefined): boolean {
  if (!id) return false;
  try {
    const registry = ServiceRegistry.getInstance();
    const sessionId = registry.get('NetworkManager', { optional: true })?.getRoom?.()?.sessionId;
    if (sessionId && id === sessionId) return true;
    const localId = registry.get('EntitySystem', { optional: true })?.getPlayerManager?.()?.getPlayer?.()?.id;
    return !!localId && id === localId;
  } catch {
    return false;
  }
}

/** Whether a confirmed hit's screen-wide feel belongs on this screen. */
export function impactFeelIsLocal(attackerId: string | null | undefined, targetId: string | null | undefined): boolean {
  if (!attackerId) return true;
  return isLocalActor(attackerId) || isLocalActor(targetId);
}
